import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { useToast } from '../ui/Toast.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useAIActivityStore } from '../../stores/useAIActivityStore.js';
import { usePendingImages, useUploadProjectImage, useGenerateImage } from '../../hooks/useProjects.js';

interface Props {
  projectId: number;
  version?: number;
  onUploaded: () => void;
  /** Show AI Generate buttons when a provider is configured. */
  aiEnabled?: boolean;
  /** Current project status. When 'approved', uploads and AI generation are blocked. */
  projectStatus?: string | undefined;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Virtual run IDs — negative to avoid collision with server-side run IDs. */
const SINGLE_RUN_ID = -1;
const BATCH_RUN_ID  = -2;

// ── Cost estimation ──────────────────────────────────────────────────────────
// Output token estimates for gpt-image-1 quality=medium (May 2026 pricing).
// Source: https://openai.com/api/pricing/ → "Image tokens" section.
//   square  1024×1024  → 1056 tokens @ $40/M = ~$0.0422
//   portrait 1024×1536 → 1568 tokens @ $40/M = ~$0.0627
//   landscape 1536×1024 → 1568 tokens @ $40/M = ~$0.0627
const IMAGE_OUTPUT_PER_M_USD = 40.0;

function estimateCost(width: number, height: number): number {
  const ratio = width / height;
  const tokens = ratio > 1.3 || ratio < 0.77 ? 1568 : 1056;
  return (tokens / 1e6) * IMAGE_OUTPUT_PER_M_USD;
}

// ── Error message mapping ─────────────────────────────────────────────────────

function mapAIError(message: string, t: (key: string) => string): string {
  if (/api key|unauthorized|401|not configured/i.test(message)) {
    return t('projects.pending.errors.apiKey');
  }
  if (/rate limit|429/i.test(message)) {
    return t('projects.pending.errors.rateLimit');
  }
  if (/quota|insufficient_quota/i.test(message)) {
    return t('projects.pending.errors.quota');
  }
  return message || t('projects.pending.errors.unknown');
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PendingImagesList({ projectId, version, onUploaded, aiEnabled = false, projectStatus }: Props) {
  const isApproved = projectStatus === 'approved';
  const { t } = useTranslation();
  const toast = useToast();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { data } = usePendingImages(projectId, version);
  const upload = useUploadProjectImage(projectId, version);
  const generateAI = useGenerateImage(projectId, version);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Track when component is unmounted to abort in-flight batch safely
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Per-row: which file is currently generating (individual or current batch item)
  const [generatingFilename, setGeneratingFilename] = useState<string | null>(null);
  // Batch progress for the "AI Generate all" button label
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);

  if (!data || data.images.length === 0) return null;

  const allImages = data.images;
  const allDone = allImages.every(img => img.uploaded);
  const pendingImages = allImages.filter(img => !img.uploaded);

  // True while any AI generation is in-flight (individual OR batch)
  // anyBusy covers both individual and batch to eliminate the race window
  // where batchProgress is set but generatingFilename hasn't been set yet.
  const batchRunning = batchProgress !== null;
  const anyBusy = generatingFilename !== null || batchRunning;

  // ── Individual upload ───────────────────────────────────────────────────────

  function handleUpload(filename: string, file: File) {
    upload.mutate({ file, filename }, { onSuccess: onUploaded });
  }

  // ── Individual AI generate ──────────────────────────────────────────────────

  function handleAIGenerate(filename: string) {
    setGeneratingFilename(filename);
    useAIActivityStore.getState().addRun({
      id: SINGLE_RUN_ID,
      type: 'ai_generation',
      label: t('projects.pending.singleLabel', { filename }),
      step: null,
      startedAt: Date.now(),
      progress: null,
      pipelineId: null,
      linkTo: `/projects/${projectId}`,
    });
    generateAI.mutate(
      { filename },
      {
        onSuccess: (data) => {
          setGeneratingFilename(null);
          useAIActivityStore.getState().removeRun(SINGLE_RUN_ID);
          const costLabel = data.costUsd != null
            ? t('pendingImages.success.singleWithCost', { cost: data.costUsd.toFixed(4) })
            : t('projects.pending.aiToastSuccess', { filename });
          toast.success(costLabel);
          onUploaded();
        },
        onError: (err) => {
          setGeneratingFilename(null);
          useAIActivityStore.getState().removeRun(SINGLE_RUN_ID);
          toast.error(mapAIError(err instanceof Error ? err.message : '', t), { duration: 0 });
        },
      },
    );
  }

  // ── Batch AI generate ───────────────────────────────────────────────────────

  async function handleBatchConfirm() {
    const total = pendingImages.length;
    const estimatedTotal = pendingImages.reduce((sum, img) => sum + estimateCost(img.width, img.height), 0);
    const cost = estimatedTotal.toFixed(2);

    const ok = await confirm({
      title: t('projects.pending.aiConfirmTitle', { count: total }),
      description: t('projects.pending.aiConfirmDesc', { cost }),
      confirmLabel: t('projects.pending.aiConfirmLabel', { count: total }),
      cancelLabel: t('common.cancel'),
      tone: 'cyan',
    });
    if (!ok) return;

    const queue = pendingImages.map(img => img.filename);
    setBatchProgress({ current: 1, total });

    const store = useAIActivityStore.getState();
    store.addRun({
      id: BATCH_RUN_ID,
      type: 'ai_generation',
      label: t('projects.pending.batchLabel', { current: 1, total }),
      step: null,
      startedAt: Date.now(),
      progress: { current: 1, total },
      pipelineId: null,
      linkTo: `/projects/${projectId}`,
    });

    let successCount = 0;
    let totalCostUsd = 0;

    for (let i = 0; i < queue.length; i++) {
      if (!isMountedRef.current) break;

      const filename = queue[i];
      if (filename === undefined) break; // noUncheckedIndexedAccess guard
      const current = i + 1;

      setGeneratingFilename(filename);
      setBatchProgress({ current, total });
      useAIActivityStore.getState().updateRun(BATCH_RUN_ID, {
        label: t('projects.pending.batchLabel', { current, total }),
        progress: { current, total },
      });

      try {
        const result = await generateAI.mutateAsync({ filename });
        if (!isMountedRef.current) break;
        successCount++;
        totalCostUsd += result.costUsd ?? 0;
      } catch (err) {
        // Store cleanup is always safe (Zustand, not React state)
        useAIActivityStore.getState().removeRun(BATCH_RUN_ID);
        if (isMountedRef.current) {
          setGeneratingFilename(null);
          setBatchProgress(null);
          const msg = err instanceof Error ? err.message : '';
          toast.error(
            t('projects.pending.batchToastError', { current, total, error: mapAIError(msg, t) }),
            { duration: 0 },
          );
          if (successCount > 0) onUploaded();
        }
        return;
      }
    }

    useAIActivityStore.getState().removeRun(BATCH_RUN_ID);
    if (isMountedRef.current) {
      setGeneratingFilename(null);
      setBatchProgress(null);
      if (successCount > 0) {
        const batchToast = totalCostUsd > 0
          ? t('pendingImages.success.batchWithCost', { count: successCount, cost: totalCostUsd.toFixed(4) })
          : t('projects.pending.batchToastSuccess', { count: successCount });
        toast.success(batchToast);
        onUploaded();
      }
    }
  }

  // ── Layout ──────────────────────────────────────────────────────────────────

  const actionColWidth = aiEnabled ? '220px' : '80px';
  const gridCols = `1fr 90px 1fr 90px ${actionColWidth}`;

  return (
    <div style={{ marginTop: 'var(--evo-space-5)' }}>

      {/* ConfirmDialog portal */}
      {confirmDialog}

      {/* Header: title + optional batch button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 'var(--evo-space-3)',
      }}>
        <div style={{
          fontSize: 'var(--evo-fs-label)',
          fontWeight: 'var(--evo-fw-semibold)',
          color: 'var(--evo-fg-1)',
          fontFamily: 'var(--evo-font)',
        }}>
          {t('projects.pending.title')}
        </div>

        {aiEnabled && pendingImages.length >= 2 && !isApproved && (
          <Button
            size="sm"
            variant="ghost"
            disabled={anyBusy}
            onClick={() => void handleBatchConfirm()}
          >
            {batchRunning && batchProgress
              ? t('projects.pending.aiGenerateAllBtnRunning', batchProgress)
              : t('projects.pending.aiGenerateAllBtn')}
          </Button>
        )}
      </div>

      {isApproved && (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          background: 'rgba(234,179,8,0.06)',
          border: '1px solid rgba(234,179,8,0.25)',
          borderRadius: 'var(--evo-r-sm)',
          fontSize: 'var(--evo-fs-body)',
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)',
          marginBottom: 'var(--evo-space-3)',
        }}>
          {t('pendingImages.approvedRevision.message')}
        </div>
      )}

      {allDone ? (
        <div style={{
          padding: 'var(--evo-space-3) var(--evo-space-4)',
          background: 'rgba(34,197,94,0.06)',
          border: '1px solid rgba(34,197,94,0.2)',
          borderRadius: 'var(--evo-r-sm)',
          fontSize: 'var(--evo-fs-body)',
          color: 'var(--evo-fg-2)',
          fontFamily: 'var(--evo-font)',
        }}>
          {t('projects.pending.allDone')}
        </div>
      ) : (
        <>
          {/* Header row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 'var(--evo-space-3)',
            padding: 'var(--evo-space-2) var(--evo-space-3)',
            fontSize: 'var(--evo-fs-label)',
            color: 'var(--evo-fg-4)',
            fontFamily: 'var(--evo-font)',
            borderBottom: '1px solid var(--evo-stroke)',
          }}>
            <span>{t('projects.pending.colFile')}</span>
            <span>{t('projects.pending.colSize')}</span>
            <span>{t('projects.pending.colRole')}</span>
            <span>{t('projects.pending.colStatus')}</span>
            <span />
          </div>

          {/* Data rows */}
          {allImages.map(img => {
            const isThisGenerating = generatingFilename === img.filename;

            return (
              <div key={img.filename} style={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                gap: 'var(--evo-space-3)',
                padding: 'var(--evo-space-2) var(--evo-space-3)',
                alignItems: 'center',
                fontSize: 'var(--evo-fs-body)',
                fontFamily: 'var(--evo-font)',
                color: 'var(--evo-fg-2)',
              }}>
                <span style={{
                  color: 'var(--evo-fg-1)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--evo-fs-label)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {img.filename}
                </span>
                <span style={{ color: 'var(--evo-fg-3)', fontSize: 'var(--evo-fs-label)' }}>
                  {img.width}×{img.height}
                </span>
                <span style={{ color: 'var(--evo-fg-3)' }}>{img.role}</span>
                <span style={{
                  color: img.uploaded ? 'var(--evo-success, #22c55e)' : 'var(--evo-fg-4)',
                  fontSize: 'var(--evo-fs-label)',
                }}>
                  {img.uploaded
                    ? t('projects.pending.statusUploaded')
                    : t('projects.pending.statusPending')}
                </span>

                {/* Action buttons — horizontal, no wrap */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--evo-space-2)',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}>
                  {!img.uploaded && !isApproved && (
                    <>
                      <input
                        ref={el => { inputRefs.current[img.filename] = el; }}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleUpload(img.filename, file);
                          if (e.target) e.target.value = '';
                        }}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={upload.isPending}
                        onClick={() => inputRefs.current[img.filename]?.click()}
                      >
                        {t('projects.pending.uploadBtn')}
                      </Button>
                      {aiEnabled && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={anyBusy}
                          onClick={() => handleAIGenerate(img.filename)}
                        >
                          {isThisGenerating
                            ? t('projects.pending.aiGenerating')
                            : t('projects.pending.aiGenerateBtn')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {/* Upload error inline — toast not used here to preserve existing UX */}
          {upload.isError && (
            <div style={{
              marginTop: 'var(--evo-space-2)',
              padding: 'var(--evo-space-2) var(--evo-space-3)',
              fontSize: 'var(--evo-fs-label)',
              color: 'var(--evo-error, #ef4444)',
              fontFamily: 'var(--evo-font)',
            }}>
              {t('projects.pending.uploadError')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
